import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AppToasterService } from '../../../core/services/toaster.service';
import { AdminPageShellComponent } from '../../../shared/components/admin/admin-page-shell/admin-page-shell.component';
import { AdminToggleSwitchComponent } from '../../../shared/components/admin/admin-toggle-switch/admin-toggle-switch.component';
import { CtaButtonComponent } from '../../../shared/components/ui/cta-button/cta-button.component';
import {
  AdminFeatureFlag,
  FeatureFlagsAdminService,
} from '../../../shared/services/feature-flags-admin.service';

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    MatProgressSpinnerModule,
    AdminPageShellComponent,
    AdminToggleSwitchComponent,
    CtaButtonComponent,
  ],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.scss',
})
export class SettingsComponent implements OnInit {
  #flagsAdmin = inject(FeatureFlagsAdminService);
  #toaster = inject(AppToasterService);
  #translate = inject(TranslateService);

  readonly flags = signal<AdminFeatureFlag[]>([]);
  readonly isLoading = signal(true);
  readonly hasError = signal(false);
  readonly toggleBusyKey = signal<string | null>(null);

  ngOnInit(): void {
    void this.loadFlags();
  }

  async loadFlags(): Promise<void> {
    this.isLoading.set(true);
    this.hasError.set(false);

    try {
      const rows = await this.#flagsAdmin.list();
      this.flags.set(rows);
    } catch {
      this.hasError.set(true);
    } finally {
      this.isLoading.set(false);
    }
  }

  async toggleFlag(flag: AdminFeatureFlag, enabled: boolean): Promise<void> {
    if (this.toggleBusyKey()) {
      return;
    }

    const previous = flag.enabled;
    this.toggleBusyKey.set(flag.key);
    this.#patchFlag(flag.key, { enabled });

    try {
      await this.#flagsAdmin.upsert({ ...flag, enabled });
      this.#toaster.success(
        this.#translate.instant('app.admin.settings.flagUpdated'),
      );
    } catch {
      this.#patchFlag(flag.key, { enabled: previous });
      this.#toaster.error(
        this.#translate.instant('app.admin.settings.flagUpdateError'),
      );
    } finally {
      this.toggleBusyKey.set(null);
    }
  }

  #patchFlag(key: string, patch: Partial<AdminFeatureFlag>): void {
    this.flags.update((rows) =>
      rows.map((row) => (row.key === key ? { ...row, ...patch } : row)),
    );
  }
}
